import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { depotFactureService } from '../../api/services';
import { DepotFacture } from '../../api/portal';
import { MoneyDisplay } from '../../components/common/MoneyDisplay';

/**
 * Back-office des dépôts de facture fournisseur (TF-055) : factures déposées via le
 * portail fournisseur, pré-lues par OCR. L'acheteur consulte le fichier, saisit la
 * facture d'achat (→ rapprochement 3 voies) puis marque le dépôt traité, ou le rejette.
 */
const STATUT_STYLE: Record<DepotFacture['statut'], string> = {
  RECU: 'bg-amber-100 text-amber-800',
  TRAITE: 'bg-green-100 text-green-800',
  REJETE: 'bg-red-100 text-red-800',
};
const STATUT_LABEL: Record<DepotFacture['statut'], string> = {
  RECU: 'Reçue', TRAITE: 'Traitée', REJETE: 'Rejetée',
};

const DepotsFactures: React.FC = () => {
  const [depots, setDepots] = useState<DepotFacture[]>([]);
  const [busy, setBusy] = useState(false);

  const load = () =>
    depotFactureService.list().then(setDepots)
      .catch((e: any) => { if (e?.response?.status !== 403) toast.error('Chargement impossible.'); });

  useEffect(() => { load(); }, []);

  const voir = async (d: DepotFacture) => {
    try {
      const blob = await depotFactureService.fichier(d.id);
      window.open(URL.createObjectURL(blob), '_blank');
    } catch { toast.error('Fichier indisponible.'); }
  };

  const traiter = async (d: DepotFacture) => {
    setBusy(true);
    try { await depotFactureService.traiter(d.id); toast.success('Dépôt marqué traité.'); load(); }
    catch (e: any) { toast.error(e?.response?.data?.message || 'Action impossible.'); }
    finally { setBusy(false); }
  };

  const rejeter = async (d: DepotFacture) => {
    const note = window.prompt('Motif du rejet (visible par le fournisseur) :');
    if (note == null) return;
    setBusy(true);
    try { await depotFactureService.rejeter(d.id, note || undefined); toast.success('Dépôt rejeté.'); load(); }
    catch (e: any) { toast.error(e?.response?.data?.message || 'Action impossible.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dépôts de factures fournisseurs</h1>
        <p className="text-sm text-gray-500">
          Factures déposées via le portail fournisseur, montants pré-lus par OCR. Saisissez la{' '}
          <Link to="/stock/achats/new" className="text-blue-600 hover:underline">facture d'achat</Link>{' '}
          puis marquez le dépôt traité — le rapprochement 3 voies suit sur la fiche fournisseur.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">
            <tr>
              <th className="px-4 py-3">Fournisseur</th>
              <th className="px-4 py-3">Fichier</th>
              <th className="px-4 py-3">N° lu</th>
              <th className="px-4 py-3">Date lue</th>
              <th className="px-4 py-3 text-right">HT lu</th>
              <th className="px-4 py-3 text-right">TTC lu</th>
              <th className="px-4 py-3">OCR</th>
              <th className="px-4 py-3">Statut</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {depots.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">
                Aucun dépôt — partagez le lien du portail depuis la fiche fournisseur.
              </td></tr>
            )}
            {depots.map((d) => (
              <tr key={d.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-900">{d.fournisseurNom || '—'}</td>
                <td className="px-4 py-3">
                  <button onClick={() => voir(d)} className="text-blue-600 hover:underline">{d.nomFichier}</button>
                </td>
                <td className="px-4 py-3 font-mono text-xs">{d.numero || '—'}</td>
                <td className="px-4 py-3 text-gray-500">
                  {d.dateFacture ? new Date(d.dateFacture).toLocaleDateString() : '—'}
                </td>
                <td className="px-4 py-3 text-right">
                  {d.totalHT != null ? <MoneyDisplay amount={d.totalHT} size="sm" /> : '—'}
                </td>
                <td className="px-4 py-3 text-right">
                  {d.totalTTC != null ? <MoneyDisplay amount={d.totalTTC} size="sm" /> : '—'}
                </td>
                <td className="px-4 py-3 text-xs text-gray-400">{d.ocrProvider || '—'}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${STATUT_STYLE[d.statut]}`}>
                    {STATUT_LABEL[d.statut]}
                  </span>
                  {d.note && <p className="text-xs text-gray-400 mt-0.5">{d.note}</p>}
                </td>
                <td className="px-4 py-3 text-right space-x-3">
                  {d.statut === 'RECU' && (
                    <>
                      <button onClick={() => traiter(d)} disabled={busy} className="text-green-700 hover:underline">Traiter</button>
                      <button onClick={() => rejeter(d)} disabled={busy} className="text-red-600 hover:underline">Rejeter</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DepotsFactures;
